define("Events", [`Model`, `module`], function (Super, Self) {
	const PRICE_LEVEL_DISPLAY = {
		SHOW_RANGE: `SHOW_RANGE`,
		SHOW_ADVERTISED_PRICE: `SHOW_ADVERTISED_PRICE`,
	};
	return Super.inherit({
		namespace: Self.id,
		singleton: true,
		requires: [`Templates`, `Time`],
		prepare(setup) {
			const { data } = this;

			this.assign(data, { previewMode: false, useAgentPriceCode: false });
		},
		api: {
			getFeatured: {
				async: true,
				public: true,
				value(context, options) {
					return this.XHR.ajax({
						type: `GET`,
						url: `/api/featured`,
					});
				},
			},

			get: {
				async: true,
				clone: true,
				public: true,
				defaults: {
					by: `id`,
					params: {},
				},
				async value(context, options) {
					await this.require({ deps: [`Groups`, `AgentPriceCode`] });
					const { Groups, XHR, data } = this;
					var promise;

					switch (options.by) {
						case `id`: {
							const { force, id } = options;
							let { params = {} } = options;
							let cacheKey = id;

							if (!this.isInt(id, 1)) {
								throw new Error(`Events.get(): Unsigned int required for options.id`);
							}

							// add the agent price code to the params if applicable
							params = this.addAgentPriceCodeParam(params);

							// ensure the cacheKey is unique to the price code
							if (params.pli) {
								cacheKey += `_pli:${params.pli}`;
							}

							promise = this.cache({ type: `event`, key: cacheKey, force }, function () {
								// Cache an 'event', based on options.id value
								let url = `/api/events/${id}`;

								if (data.previewMode === true) {
									params.mode = `PREVIEW`;
								}

								if (!this.isEmpty(params)) {
									this.chain(params)
										.reduce((list, value, key) => {
											if (this.isString(value, 1)) {
												list.push(`${key}=${value}`);
											}
											return list;
										}, []) // start with an empty array
										.tap((list) => {
											if (list.length > 0) {
												url += `?` + list.join(`&`);
											}
										});
								}

								return XHR.ajax({
									dataType: `json`,
									type: `GET`,
									url,
									then: (event) => {
										this.normalize(event);
										Groups.normalize(event.group);

										return event;
									},
								});
							});
							break;
						}

						case `ids`:
							if (!this.isArray(options.ids, 1)) {
								throw new Error(`Events.get(): Array of unsigned ints required for options.ids`);
							}

							this.chain(options.ids)
								.chunk(20)
								.map((ids) =>
									XHR.ajax({
										url: `/api/events`,
										type: `GET`,
										data: {
											ids: ids.join(`,`),
										},
									})
								)
								.tap((promises) => {
									promise = this.when({
										promises,
										then: (eventLists) => {
											// an array of XHR responses
											return this.reduce(
												eventLists,
												(fullList, response) => {
													this.each(response, this.normalize);
													return fullList.concat(response); // add this chunk to the full list
												},
												[]
											); // start with an empty array
										},
									});
								});
							break;
					}

					return promise;
				},
			},

			/**
			 * Set the flag to indicate if event requests should use the agent price code
			 * @method setUseAgentPriceCode
			 * @param {Boolean} useAgentPriceCode - True if agent price code should be used
			 */
			setUseAgentPriceCode: {
				public: true,
				value(_, useAgentPriceCode) {
					this.data.useAgentPriceCode = useAgentPriceCode;
				},
			},

			/**
			 * Returns the flag indicating if the agent price code is being used
			 * @method isUseAgentPriceCode
			 * @returns {Boolean} - True if agent price code is being used
			 */
			isUseAgentPriceCode: {
				public: true,
				value() {
					return this.data.useAgentPriceCode;
				},
			},

			/**
			 * Checks if the event is currently supporting agent price codes.
			 * If yes, add the price list id param to the request parameters.
			 * @method addAgentPriceCodeParam
			 * @param {Object} _params - The request parameters
			 * @returns {Object} - The modified request parameters with the agent price code if applicable
			 */
			addAgentPriceCodeParam: {
				value(params = {}) {
					const isUseAgentPriceCode = this.isUseAgentPriceCode();
					const priceCodeParams = {};

					// Assign the current agent price code if one is not passed in already
					if (isUseAgentPriceCode && params?.pli == null) {
						const agentPriceCode = this.AgentPriceCode.get();

						if (agentPriceCode?.id != null) {
							priceCodeParams.pli = `${agentPriceCode.id}`;
						}
					}

					return {
						...params,
						...priceCodeParams,
					};
				},
			},

			getLeaderboard: {
				async: true,
				public: true,
				value(context, options) {
					if (!this.isInt(options.id)) {
						throw new Error(`Events.getLeaderboard(): options.id is required`);
					}

					return this.XHR.ajax({
						url: `/api/amplify-leaderboards/${options.id}`,
						then: function (response) {
							if (this.isNull(response)) {
								response = {
									eventId: options.id,
									entries: [],
								};
							}

							return response;
						},
					});
				},
			},

			getLive: {
				async: true,
				public: true,
				defaults: {
					page: `1`,
				},
				value(context, options) {
					const { page } = options;

					return this.XHR.ajax({
						type: `GET`,
						url: `/api/events`,
						data: { page },
						then: (response) => this.map(response, this.normalize),
					});
				},
			},

			searchEvents: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR } = this;
					const fields = [`artistName`, `city`, `eventName`, `page`, `pageSize`, `venueName`]; // for BE cache, params needs to be in alphabetical order
					const params = {};

					this.each(fields, (field) => {
						if (this.isDefined(options[field])) {
							params[field] = options[field];
						}
					});

					return XHR.ajax({
						type: `GET`,
						url: `/api/events?${this.App.serializeParams(params)}`,
						dataType: `json`,
						block: options.block,
						then: (response) => this.map(response, this.normalize),
					});
				},
			},

			normalize: {
				public: true,
				value(context, event) {
					const group = this.isObject(event.group) ? event.group : {};
					const { App, Config, Time, Utils, formatDateTimeWithLocale } = this;
					const { currencies, doorsOpenDate, hiddenDetails, holds, id, rewardsPackage, sales, venue } = event;
					const { timezone } = venue;
					const emailRegex = /^support@tixr.com$/i;
					const isAgent = this.isString(App.getCookie(`agentHandle`), 1);
					let hiddenDetailsList;
					let dateLabel = null;
					let dateLabelLocale = null;
					let dateLabelMedium = null;
					let dateLabelLocaleMedium = null;
					let doorsOpenLabel = null;
					let doorsOpenLabelLocale = null;

					event.currency = this.isArray(currencies) ? currencies[0] : currencies;
					event.trackingId = `e${id}`;

					if (this.isString(hiddenDetails, 1)) {
						hiddenDetailsList = hiddenDetails.split(`,`);
					} else {
						hiddenDetailsList = [];
					}

					if (isAgent) {
						hiddenDetailsList = hiddenDetailsList.concat([`AVATAR`, `FOLLOW`, `MORE_EVENTS`, `REWARDS`, `SHARE`]);
					}

					event.hiddenDetailsList = hiddenDetailsList;

					if (!this.isArray(event.currencies)) {
						event.currencies = Config.DEFAULT_CURRENCIES;
					}

					// Do any data prep necessary
					event.duration = event.endDate - event.startDate;
					event.multiday = event.duration > 86400000; // 1000 ms * 60 sec * 60 min * 24 hour
					if (event.ageRestriction > 0) {
						event.ageRestriction = `Ages ${event.ageRestriction} &amp; over`;
					} else {
						event.ageRestriction = `All ages`;
					}

					this.each(event.media, function (item) {
						// TODO: remove when proper thumbnail support is in place
						if (item.type === `IMAGE` && this.isUndefined(item.thumbnailUrl)) {
							item.thumbnailUrl = item.url;
						}
					});

					if (event.eventCategoryName !== `Splash`) {
						// never display date for splash page
						[dateLabel, dateLabelLocale] = this.normalizeEventDateTime(event, { alwaysIncludeWeekday: false });
						[dateLabelMedium, dateLabelLocaleMedium] = this.normalizeEventDateTime(event, {
							alwaysIncludeWeekday: true,
						});
					}

					if (this.isInt(doorsOpenDate)) {
						// check if doors open and event start are the same day in the venue timezone
						const startDateString = Time.format(`%Y%N%D`, event.startDate, timezone);
						const doorsDateString = Time.format(`%Y%N%D`, event.doorsOpenDate, timezone);
						const [doorsTime, doorsTimeLocale] = formatDateTimeWithLocale({
							format: `TIME_SHORT`,
							timestamp: event.doorsOpenDate,
							timezone,
							varName: `dt_time`,
						});
						const doorsPrefix = `Doors open`;
						const labelPieces = [doorsPrefix];
						const labelPiecesLocale = [doorsPrefix];

						if (startDateString !== doorsDateString) {
							// doors and start are on different days, explicitly mention the weekday
							const [doorsWeekday, doorsWeekdayLocale] = formatDateTimeWithLocale({
								format: `%w`,
								timestamp: event.doorsOpenDate,
								timezone,
								varName: `dt_weekday`,
							});

							labelPieces.push(`on ${doorsWeekday}`);
							labelPiecesLocale.push(`on ${doorsWeekdayLocale}`);
						}

						labelPieces.push(`at ${doorsTime}`);
						labelPiecesLocale.push(`at ${doorsTimeLocale}`);

						doorsOpenLabel = labelPieces.join(` `);
						doorsOpenLabelLocale = labelPiecesLocale.join(` `);
					}

					this.assign(event, {
						dateLabel,
						dateLabelLocale,
						dateLabelMedium,
						dateLabelLocaleMedium,
						doorsOpenLabel,
						doorsOpenLabelLocale,
					});

					this.normalizeSales({ sales, event });

					this.normalizeRewards({ event });

					if (this.isArray(holds, 1)) {
						// holds are present
						this.normalizeHolds({ holds, event });
					} else {
						// no holds found, insert empty array
						event.holds = [];
					}

					if (this.isString(group.logoUrl, 1)) {
						const { logoUrl } = group;
						const index = logoUrl.lastIndexOf(`/`);
						// construct the path to the group logo thumbnail
						group.logoThumbnailUrl = Utils.insertAt(logoUrl, index, `/thumbnail`);
					}

					// NOTE: Temporary fix, that should be reviewe once all the venues are audited
					if (this.isObject(venue) && emailRegex.test(venue.email)) {
						venue.email = null; // reset to no email, to render default support
					}

					if (this.isObject(venue) && this.isObject(venue.address)) {
						event.prefersEnGb = [`IE`, `UK`, `GB`, `AU`, `NZ`].includes(venue.address.countryCodeAlpha2);
					} else {
						event.prefersEnGb = false;
					}

					if (this.isObject(rewardsPackage)) {
						this.chain(rewardsPackage.rewards)
							.sortBy(`pointsRequired`)
							.tap(function (sorted) {
								rewardsPackage.rewards = sorted;
							});
					}

					if (!this.isFinite(event.backgroundContrastPercentage)) {
						event.backgroundContrastPercentage = event.backgroundContrast ? 80 : 0;
					}

					event.overlayBackgroundContrastPercentage = event.backgroundContrastPercentage + 20;
					if (event.overlayBackgroundContrastPercentage > 100) {
						event.overlayBackgroundContrastPercentage = 100;
					}

					return event;
				},
			},

			/**
			 * Normalize the event date and time for display
			 * @param {Object} event - The event object
			 * @param {Object} options - The options object
			 * @param {Boolean} [options.alwaysIncludeYear=false] - Always include the year in the date
			 * @param {Boolean} [options.alwaysIncludeWeekday=false] - Always include the weekday in the date
			 * @param {String} [options.varName] - The variable name for the locale
			 * @returns {Array} - An array containing the date and time strings @see formatDateTimeWithLocale
			 */
			normalizeEventDateTime: {
				public: true,
				value(_, event, options) {
					const { startDate, venue, hiddenDetails, multiday } = event;
					const { timezone } = venue;
					const { alwaysIncludeYear, alwaysIncludeWeekday, varName } = options;
					const includeDate = !this.includes(hiddenDetails, `DATE`);
					let includeTime = !this.includes(hiddenDetails, `TIME`);
					let { includeWeekday } = options;
					let endDate;

					if (multiday) {
						endDate = event.endDate; // only set the end date if it's a multiday event
						includeTime = false; // do not show the time for multiday events

						if (!alwaysIncludeWeekday) {
							includeWeekday = false; // do not show the weekday for multiday events unless explicitly requested
						}
					}

					return this.normalizeDateTime({
						startDate,
						endDate,
						timezone,
						includeDate,
						includeTime,
						includeWeekday,
						alwaysIncludeYear,
						varName,
					});
				},
			},

			/**
			 * Normalize the date and time for display
			 * @param {Object} options - The options object
			 * @param {Number} options.startDate - The start date timestamp
			 * @param {Number} [options.endDate] - The end date timestamp
			 * @param {String} options.timezone - The timezone
			 * @param {Boolean} [options.includeDate=true] - Include the date in the output
			 * @param {Boolean} [options.includeTime=true] - Include the time in the output
			 * @param {Boolean} [options.includeWeekday=true] - Include the weekday in the output
			 * @param {Boolean} [options.alwaysIncludeYear=false] - Always include the year in the date
			 * @param {Boolean} [options.useFull=true] - Use the full date and time format
			 * @param {String} [options.varName] - The variable name for the locale
			 * @returns {Array} - An array where the first element is the formatted date and time, and the second element the markup for the locale to be used by DateTimeLocaleObserver
			 */
			normalizeDateTime: {
				public: true,
				value(_, options) {
					const {
						startDate,
						endDate,
						timezone,
						includeDate = true,
						includeTime = true,
						includeWeekday = true,
						alwaysIncludeYear = false, // if false, only include year if it's different from current year
						useFull = true, // only used when includeDate and includeTime are both true
						varName, // var name for the locale
					} = options;
					const showYear = alwaysIncludeYear ? true : this.isShowYear(startDate, endDate, timezone);
					const format = [];

					if (!includeDate && !includeTime) {
						return [``, ``];
					}

					if (includeDate) {
						let dateFormat = `DATE`;

						if (showYear) {
							if (includeWeekday) {
								dateFormat = `DATE_LONG`;
							}
						} else {
							if (includeWeekday) {
								dateFormat = `DATE_MEDIUM`;
							} else {
								dateFormat = `DATE_SHORT`;
							}
						}

						format.push(dateFormat);
					}

					if (includeTime) {
						format.push(`TIME`);

						if (useFull && includeDate) {
							format.push(`FULL`);
						}
					}

					const formatOptions = {
						format: format.join(`_`),
						timestamp: startDate,
						timezone,
						varName,
					};

					if (this.isInt(endDate)) {
						this.assign(formatOptions, { startTimestamp: startDate, endTimestamp: endDate });

						return this.formatDateTimeRangeWithLocale(formatOptions);
					}

					return this.formatDateTimeWithLocale(formatOptions);
				},
			},

			/**
			 * Check if the year should be shown in the date. Year is shown if the start date is in a different year than the current year
			 * @param {Number} startDate - The start date timestamp
			 * @param {Number} [endDate] - The end date timestamp
			 * @param {String} timezone - The timezone
			 * @returns {Boolean} - True if the year should be shown, false otherwise
			 */
			isShowYear: {
				private: true,
				value(startDate, endDate, timezone) {
					const { Time } = this;
					const { format: timeFormat } = Time;

					if (!this.isInt(endDate)) {
						endDate = startDate;
					}

					const startYear = timeFormat(`%Y`, startDate, timezone);
					const endYear = timeFormat(`%Y`, endDate, timezone);
					const currentYear = timeFormat(`%Y`, null, timezone);

					return startYear !== currentYear || startYear !== endYear;
				},
			},

			/**
			 * Format the date based on the event configuration
			 * @param {Object} context - The context object
			 * @param {Object} options - The options object
			 * @param {Object} options.event - The event object
			 * @param {String} options.event.startDate - The start date timestamp
			 * @param {String} options.event.endDate - The end date timestamp
			 * @param {String} options.event.venue - The venue object
			 * @param {String} options.event.venue.timezone - The timezone
			 * @param {Array} options.event.hiddenDetailsList - The list of hidden details
			 */
			formatDateBasedOnEventConfig: {
				public: true,
				value(context, options) {
					const { Time, Templates } = this;
					const { event } = options;
					const { endDate, hiddenDetailsList, startDate, venue } = event;
					let date;

					if (this.includes(hiddenDetailsList, `DATE`) && this.includes(hiddenDetailsList, `TIME`)) {
						date = null;
					} else if (Time.isDateRangeMultiDay({ startDate, endDate })) {
						if (!this.includes(hiddenDetailsList, `DATE`)) {
							date = Templates.getDateTimeRangeLocale({
								format: `DATE_SHORT`,
								startTimestamp: startDate,
								endTimestamp: endDate,
								timezone: venue.timezone,
							});
						}
					} else if (this.includes(hiddenDetailsList, `DATE`)) {
						date = Templates.getDateTimeLocale({
							format: `TIME`,
							timestamp: startDate,
							timezone: venue.timezone,
						});
					} else if (this.includes(hiddenDetailsList, `TIME`)) {
						date = Templates.getDateTimeLocale({
							format: `DATE_MEDIUM`,
							timestamp: startDate,
							timezone: venue.timezone,
						});
					} else {
						date = Templates.getDateTimeLocale({
							format: `DATE_LONG_TIME_FULL`,
							timestamp: startDate,
							timezone: venue.timezone,
						});
					}

					return date;
				},
			},

			/**
			 * Format the date and time with the locale
			 * @param {Object} options - The options object
			 * @param {String} options.format - The format string
			 * @param {Number} options.timestamp - The timestamp
			 * @param {String} options.timezone - The timezone
			 * @param {String} [options.varName] - The variable name for the locale
			 * @returns {Array} - An array where the first element is the formatted date and time, and the second element the markup for the locale to be used by DateTimeLocaleObserver
			 */
			formatDateTimeWithLocale: {
				private: true,
				value(options) {
					const { format, timestamp, timezone } = options;
					const { Time, Templates } = this;

					return [Time.formatLocale(format, timestamp, timezone), Templates.getDateTimeLocale(options)];
				},
			},

			/**
			 * Format the date and time range with the locale
			 * @param {Object} options - The options object
			 * @param {String} options.format - The format string
			 * @param {Number} options.startTimestamp - The start timestamp
			 * @param {Number} options.endTimestamp - The end timestamp
			 * @param {String} options.timezone - The timezone
			 * @param {String} [options.varName] - The variable name for the locale
			 * @returns {Array} - An array where the first element is the formatted date and time range, and the second element the markup for the locale to be used by DateTimeLocaleObserver
			 */
			formatDateTimeRangeWithLocale: {
				private: true,
				value(options) {
					const { format, startTimestamp, endTimestamp, timezone } = options;
					const { Time, Templates } = this;

					return [
						Time.formatLocaleRange(format, startTimestamp, endTimestamp, timezone),
						Templates.getDateTimeRangeLocale(options),
					];
				},
			},

			normalizeProducts: {
				value({ event, products }) {
					const { Time } = this;
					const { format: timeFormat } = Time;
					const { endDate, multiday, venue } = event;
					const { timezone } = venue;
					const isUnlocked = false; // static init value
					const color = null; // static init value

					this.each(products, (product) => {
						const { entryDate, initialQuantity } = product;
						const startDate = this.isInt(entryDate) ? entryDate : event.startDate;
						const startYear = timeFormat(`%Y`, startDate, timezone);
						const currentYear = timeFormat(`%Y`, null, timezone);

						let dateLabel;

						if (initialQuantity === undefined) {
							product.initialQuantity = 0;
						}

						if (startYear === currentYear) {
							dateLabel = timeFormat(`%w, %m %d, %h:%i%a`, startDate, timezone);
						} else {
							dateLabel = timeFormat(`%w, %m %d, %Y %h:%i%a`, startDate, timezone);
						}

						if (multiday) {
							const startMonth = timeFormat(`%m`, startDate, timezone);
							const endMonth = timeFormat(`%m`, endDate, timezone);
							const endYear = timeFormat(`%Y`, endDate, timezone);
							const showMonth = startMonth !== endMonth;
							const showYear = endYear !== startYear;

							if (showMonth) {
								if (showYear) {
									dateLabel += timeFormat(` - %m %d, %Y %h:%i%a`, endDate, timezone);
								} else {
									dateLabel += timeFormat(` - %m %d %h:%i%a`, endDate, timezone);
								}
							} else {
								// starts and ends in different months
								if (showYear) {
									dateLabel += timeFormat(` - %d, %Y %h:%i%a`, endDate, timezone);
								} else {
									dateLabel += timeFormat(` - %d %h:%i%a`, endDate, timezone);
								}
							}
						} else {
							dateLabel += timeFormat(` - %h:%i%a`, endDate, timezone);
						}

						this.assign(product, { color, dateLabel, isUnlocked });
					});

					return products;
				},
			},

			// TODO: Update this function to match changes made in normalize to handle year changes
			normalizeHolds: {
				public: true,
				value(context, { event, holds }) {
					const { Config } = this;
					const { SALE_CATEGORY_ICONS } = Config;
					const { currencies, id: eventId } = event;
					const [currency] = currencies;
					const DEFAULT_ICON = `ticket`;

					// static properties across all holds
					const formattedPriceResale = null;
					const minPriceResale = null;
					const maxPriceResale = null;
					const productType = `HOLD`;
					const isAddOn = false;
					const type = `FIXED`;
					const video = false;

					// run the common logic between Sales and Holds
					this.normalizeProducts({ event, products: holds });

					this.each(holds, (hold) => {
						// iterate over each sale, for any special handling
						const { id, maxPrice, minPrice, priceLevelOptions, saleAssignments } = hold;
						const isLevels = this.isArray(priceLevelOptions, 1);
						const trackingId = `e${eventId}_h${id}`;

						let isReserved = false;
						let advertisedPrice;
						let formattedPrice;
						let category;
						let icon;

						this.extract(saleAssignments, (assignment, i) => {
							const id = assignment.saleId;
							const sale = this.find(event.sales, { id });
							let deliveryConfiguration = null; // default value
							let tier;

							if (!this.isObject(sale)) {
								// NOTE: Door assignments are included, but door sales are not
								return true; // remove from the array
							}

							if (!isReserved && sale.isReserved) {
								isReserved = true;
							}

							if (hold.deliveryConfigurationBehavior === `INHERIT`) {
								deliveryConfiguration = sale.deliveryConfiguration;
							} else {
								deliveryConfiguration = hold.deliveryConfiguration;
							}

							if (this.isUndefined(category)) {
								category = sale.category; // use the first encountered category
							}

							tier = sale.tiers[0];
							// convenience properties for easier checkout
							this.assign(assignment, { deliveryConfiguration, tierId: tier.id });
						});

						icon = this.isString(category, 1) ? SALE_CATEGORY_ICONS[category] : DEFAULT_ICON;

						if (minPrice === maxPrice) {
							// single price
							advertisedPrice = minPrice;
							formattedPrice = this.formatPrice({ price: minPrice, type: `currencyCompact`, currency });
						} else {
							// price range
							const minPriceLabel = this.formatPrice({ price: minPrice, type: `currencyCompact`, currency });
							const maxPriceLabel = this.formatPrice({ price: maxPrice, type: `currencyCompact`, currency });

							// TODO: more dynamic advertisedPrice necessary?
							advertisedPrice = minPrice;
							formattedPrice = `${minPriceLabel} - ${maxPriceLabel}`;
						}

						if (isLevels) {
							hold.priceLevels = this.map(priceLevelOptions, (level, i) => {
								const { id: value, maxPrice, minBehavior, minTicketsAllowed, minPrice, name: label } = level;
								let { initialQuantity } = level;
								let formattedLevelPrice;

								if (initialQuantity === undefined) {
									level.initialQuantity = 0;
									initialQuantity = 0;
								}

								if (minPrice === maxPrice) {
									formattedLevelPrice = this.formatPrice({ price: minPrice, type: `currencyCompact`, currency });
								} else {
									const minPriceLabel = this.formatPrice({ price: minPrice, type: `currencyCompact`, currency });
									const maxPriceLabel = this.formatPrice({ price: maxPrice, type: `currencyCompact`, currency });

									formattedLevelPrice = `${minPriceLabel} - ${maxPriceLabel}`;
								}

								return {
									initialQuantity,
									isAddOn,
									label,
									formattedPrice: formattedLevelPrice,
									minBehavior,
									minTicketsAllowed,
									value,
								};
							});
						} else {
							hold.priceLevels = null;
						}

						this.assign(hold, {
							advertisedPrice,
							category,
							formattedPrice,
							icon,
							isLevels,
							isReserved,
							productType,
							formattedPriceResale,
							minPriceResale,
							maxPriceResale,
							trackingId,
							type,
							video,
						});
					});

					return holds;
				},
			},

			// TODO: Update this function to match changes made in normalize to handle year changes
			normalizeSales: {
				public: true,
				value(context, { event, sales }) {
					const { Config } = this;
					const { VIDEO_DELIVERY_TYPES } = Config;
					const { currencies, id: eventId } = event;
					const [currency] = currencies;

					// run the common logic between Sales and Holds
					this.normalizeProducts({ event, products: sales });

					// iterate over each sale, for any special handling
					this.each(sales, (sale) => {
						const { category, deliveryConfiguration, id, resaleState, state, type, unitsPerItem, zone } = sale;
						const isResaleAvailable = resaleState === `AVAILABLE`;
						const isAddOn = type === `ADD_ON`;

						// join with the icon map, so each sale has quick reference to their own
						const icon = Config.SALE_CATEGORY_ICONS[category];
						const [tier] = sale.tiers;
						const { maxPrice, minPrice, name, price, priceLevels, priceListEntry } = tier;
						const isLevels = this.isArray(priceLevels, 1);
						const isReserved = this.isObject(zone);
						const productType = `SALE`;
						const trackingId = `e${eventId}_s${id}`;
						let formattedPricePerUnit;
						let formattedPriceResale;
						let formattedPriceTotal;
						let advertisedPrice;
						let formattedPrice;
						let minPriceResale;
						let maxPriceResale;
						let video;

						// convenience properties for easier checks
						if (!this.isEmpty(priceListEntry)) {
							sale.priceListId = priceListEntry.priceListId;
							sale.priceCode = priceListEntry.priceCode;
						}

						if (isLevels) {
							sale.price = null;
							sale.priceLevels = this.map(priceLevels, (level) => {
								const {
									advertisedPrice: isAdvertisedPrice,
									minBehavior,
									minTicketsAllowed,
									price: levelPrice,
									priceLevelOptionId: value,
									priceLevelOptionName: label,
									priceRegulationBreakout,
								} = level;
								const formattedLevelPrice = this.formatPrice({ price: levelPrice, type: `currencyCompact`, currency });
								let { initialQuantity } = level;

								if (initialQuantity === undefined) {
									level.initialQuantity = 0;
									initialQuantity = 0;
								}

								if (isAdvertisedPrice === true) {
									advertisedPrice = levelPrice;
								}

								const priceBreakoutMessage = this.getPriceBreakoutMessage({ priceRegulationBreakout, currency });

								return {
									label,
									formattedPrice: formattedLevelPrice,
									initialQuantity,
									minBehavior,
									minTicketsAllowed,
									priceBreakoutMessage,
									value,
								};
							});
						} else {
							advertisedPrice = price;
							sale.priceLevels = null;
							sale.price = price;
						}

						if (isResaleAvailable) {
							// no first party tickets available, use resale pricing info
							const { resalePriceBreakout } = sale;
							const { maxPrice: breakoutMaxPrice, minPrice: breakoutMinPrice } = resalePriceBreakout;
							const maxPriceLabel = this.formatPrice({ price: breakoutMaxPrice, type: `currencyCompact`, currency });

							if (breakoutMaxPrice === breakoutMinPrice) {
								formattedPriceResale = maxPriceLabel;
							} else {
								const minPriceLabel = this.formatPrice({ price: breakoutMinPrice, type: `currencyCompact`, currency });

								formattedPriceResale = `${minPriceLabel} - ${maxPriceLabel}`;
							}
						} else {
							formattedPriceResale = null;
							minPriceResale = null;
							maxPriceResale = null;
						}

						if (isResaleAvailable && state === `SOLD_OUT`) {
							formattedPrice = formattedPriceResale;
						} else if (minPrice === maxPrice) {
							// single price
							formattedPrice = this.formatPrice({
								price: minPrice,
								type: `currencyCompact`,
								currency,
								freeLabel: isAddOn ? `Included` : undefined,
							});
						} else {
							// price range
							const minPriceLabel = this.formatPrice({
								price: minPrice,
								type: `currencyCompact`,
								currency,
								freeLabelFormat: `NUMBER`,
							});
							const maxPriceLabel = this.formatPrice({ price: maxPrice, type: `currencyCompact`, currency });

							if (event.priceLevelDisplay === PRICE_LEVEL_DISPLAY.SHOW_ADVERTISED_PRICE) {
								formattedPrice = this.formatPrice({ price: advertisedPrice, type: `currencyCompact`, currency });
							} else {
								formattedPrice = `${minPriceLabel} - ${maxPriceLabel}`;
							}
						}

						if (this.isObject(deliveryConfiguration)) {
							const { additionalDeliveryType, deliveryType } = deliveryConfiguration;
							if (this.includes(VIDEO_DELIVERY_TYPES, deliveryType)) {
								video = true;
							} else if (this.includes(VIDEO_DELIVERY_TYPES, additionalDeliveryType)) {
								video = true;
							} else {
								video = false;
							}
						} else {
							video = false;
						}

						if (
							!(isResaleAvailable && state === `SOLD_OUT`) && // no first party available
							Number.isSafeInteger(unitsPerItem) &&
							unitsPerItem > 1 // per-unit pricing details are available
						) {
							const { shortUnitLabel } = sale;
							const perUnitPrice = advertisedPrice / unitsPerItem;
							const advertisedPriceFormatted = this.formatPrice({
								price: advertisedPrice,
								type: `currencyCompact`,
								currency,
							});
							const perUnitPriceFormatted = this.formatPrice({
								price: perUnitPrice,
								type: `currencyCompact`,
								currency,
							});
							const unitLabel = this.isString(shortUnitLabel) ? shortUnitLabel : `person`;
							formattedPricePerUnit = `${perUnitPriceFormatted}&nbsp;/&nbsp;${unitLabel}`;
							formattedPriceTotal = `${advertisedPriceFormatted}&nbsp;total`;
						} else {
							formattedPricePerUnit = null;
							formattedPriceTotal = null;
						}

						this.assign(sale, {
							advertisedPrice,
							formattedPrice,
							formattedPricePerUnit,
							formattedPriceTotal,
							icon,
							isAddOn,
							isLevels,
							isReserved,
							productType,
							formattedPriceResale,
							maxPriceResale,
							minPriceResale,
							trackingId,
							video,

							// convenience shortcuts to tiers[0]
							maxPrice,
							minPrice,
							name,
							price,
						});
					});

					return sales;
				},
			},

			normalizeRewards: {
				value({ event }) {
					const { rewardsPackage, sales } = event;

					if (this.isObject(rewardsPackage)) {
						const { pointAssignments } = rewardsPackage;

						rewardsPackage.pointAssignments = this.map(pointAssignments, (assignment) => {
							const { saleId, points } = assignment;
							const sale = this.find(sales, { id: saleId });

							return {
								points,
								saleId,
								saleName: this.isObject(sale) ? sale.tiers[0].name : null,
							};
						});
					}
				},
			},

			// TODO: replace usage with normalizeSales if possible
			normalizePostTransactionAddOns: {
				public: true,
				value(context, { event, addOns }) {
					const { currencies } = event;
					const [currency] = currencies;

					this.each(addOns, (addOn) => {
						const { priceRegulationBreakout } = addOn;
						const priceBreakoutMessage = this.getPriceBreakoutMessage({ priceRegulationBreakout, currency });
						const activeTier = this.find(addOn.tiers, { active: true });
						const priceLevels = this.clone(activeTier.priceLevels);
						const { minPrice, maxPrice, name, price } = activeTier;
						let formattedPriceTotal;

						if (this.isArray(priceLevels, 1)) {
							const formattedMinPrice = this.formatPrice({
								price: minPrice,
								type: `currencyCompact`,
								currency,
							});
							const formattedMaxPrice = this.formatPrice({
								price: maxPrice,
								type: `currencyCompact`,
								currency,
							});

							if (event.priceLevelDisplay === PRICE_LEVEL_DISPLAY.SHOW_ADVERTISED_PRICE) {
								const price = this.find(priceLevels, { advertisedPrice: true }).price;
								formattedPriceTotal = this.formatPrice({ price, type: `currencyCompact`, currency });
							} else {
								formattedPriceTotal = `${formattedMinPrice} - ${formattedMaxPrice}`;
							}
						} else {
							formattedPriceTotal = this.formatPrice({
								price,
								type: `currencyCompact`,
								currency,
							});
						}

						this.each(priceLevels, (priceLevel) => {
							priceLevel.isAddable = true;
							priceLevel.isRemovable = true;
							priceLevel.value = priceLevel.priceLevelOptionId;
							priceLevel.label = priceLevel.priceLevelOptionName;

							priceLevel.formattedPrice = this.formatPrice({
								price: priceLevel.price,
								type: `currencyCompact`,
								currency,
							});
						});

						this.assign(addOn, {
							advertisedPrice: activeTier.price,
							isLevels: this.isArray(activeTier.priceLevels, 1),
							isRemovable: true,
							localState: addOn.state,
							name,
							priceBreakoutMessage,
							productType: addOn.type || `ADD_ON`,
							quantity: 0,
							formattedPriceTotal,
							priceLevels: priceLevels,
							selections: [],
							summary: formattedPriceTotal,
						});
					});

					return addOns;
				},
			},

			formatPrice: {
				public: true,
				/**
				 * @param {*} context
				 * @param {{
				 * 	price: number,
				 * 	currency: string,
				 * 	type: 'currency' | 'currencyCompact' | 'currencyAbbreviated' | 'currencyAbbreviatedCompact' | 'currencyCentsOptional',
				 * 	freeLabelFormat: 'TEXT' | 'NUMBER',
				 * 	freeLabel: string,
				 * }} options
				 * @returns string
				 */
				value(context, { price, currency, type = `currencyCentsOptional`, freeLabelFormat = `TEXT`, freeLabel }) {
					const { Templates } = this;

					if (price === 0 && freeLabelFormat === `TEXT`) {
						return this.isString(freeLabel) ? freeLabel : `FREE`;
					} else {
						const printNumber = Templates.getHelper(`printNumber`);
						const result = printNumber(price, type, currency, null);
						return this.isObject(result) ? result.string : result;
					}
				},
			},

			formatPriceDifference: {
				defaults: {
					freeLabel: null,
				},
				public: true,
				value(context, { priceDifference, currency, freeLabel, type = `currencyCompact` }) {
					if (priceDifference === 0 && this.isString(freeLabel)) {
						return freeLabel;
					}

					const prefix = priceDifference === 0 ? `` : priceDifference < 0 ? `-` : `+`;
					const renderedPrice = this.formatPrice({ currency, price: priceDifference, type });

					return `${prefix}${renderedPrice}`;
				},
			},

			setPreviewMode: {
				public: true,
				value(context, mode) {
					this.data.previewMode = mode;
				},
			},

			getPriceBreakoutMessage: {
				public: true,
				value(context, { priceRegulationBreakout, currency }) {
					let message = null;

					if (this.isObject(priceRegulationBreakout)) {
						const { basePrice, includeFees, includeTaxes, applyItemization, feesAndTaxesToInclude } =
							priceRegulationBreakout;

						if (basePrice === 0) {
							return message;
						}

						if (includeFees) {
							if (includeTaxes) {
								if (applyItemization) {
									if (feesAndTaxesToInclude > 0) {
										message = `(${this.formatPrice({
											price: basePrice,
											type: `currencyCompact`,
											currency,
										})} + ${this.formatPrice({
											price: feesAndTaxesToInclude,
											type: `currencyCompact`,
											currency,
										})} fees & taxes)`;
									} else {
										message = `(No fees or taxes on this item)`;
									}
								} else {
									message = `(Includes fees & taxes)`;
								}
							} else {
								if (applyItemization) {
									if (feesAndTaxesToInclude > 0) {
										message = `(${this.formatPrice({
											price: basePrice,
											type: `currencyCompact`,
											currency,
										})} + ${this.formatPrice({
											price: feesAndTaxesToInclude,
											type: `currencyCompact`,
											currency,
										})} fees)`;
									} else {
										message = `(No fees on this item)`;
									}
								} else {
									message = `(Includes fees)`;
								}
							}
						}
					}

					return message;
				},
			},

			getVideo: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR } = this;
					const { eventId, groupId, saleId } = options;
					const url = `/api/groups/${groupId}/events/${eventId}/videos/${saleId}`;
					const type = `GET`;
					return XHR.ajax({ type, url });
				},
			},

			keepVideoAlive: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR } = this;
					const { eventId, groupId, saleId } = options;
					const url = `/api/groups/${groupId}/events/${eventId}/videos/${saleId}/keep-alive`;
					const type = `GET`;

					return XHR.ajax({ type, url });
				},
			},

			endVideoSession: {
				async: true,
				public: true,
				async value(context, options) {
					try {
						const { XHR } = this;
						const { eventId, groupId, saleId } = options;
						const url = `/api/groups/${groupId}/events/${eventId}/videos/${saleId}/exit`;
						const type = `POST`;

						await XHR.ajax({ type, url });
					} catch (reason) {
						// swallow the error and continue
					}
				},
			},
		},
	});
});

//# sourceMappingURL=Events.js.map